import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'prodlouzeny-vikend-na-chalupe',
    title: 'Prodloužený víkend na chalupě: jak ze tří dnů vytěžit maximum',
    metaTitle: 'Prodloužený víkend na chalupě: jak ho vybrat',
    perex:
      'Státní svátek uprostřed týdne je pozvánka: jeden den dovolené a máte pět dní volna. Poradíme, jak vybrat chalupu na prodloužený víkend, kam nejezdit daleko a na co myslet, aby vám tři dny stačily.',
    coverImage: 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Dřevěná chalupa s komínem uprostřed jehličnatého lesa',
    publishedAt: '2026-09-25',
    author: 'Redakce Mátevolno',
    tags: ['prodloužený víkend', 'podzim', 'víkend', 'tipy'],
  },
  content: `Letos připadá 28. říjen na středu. Kdo si vezme čtvrtek a pátek, má pět dní v kuse. Kdo ne, má aspoň volný den uprostřed týdne a k němu víkend na dosah. Školní podzimní prázdniny bývají na svátek navázané, takže rodiny mají volno zařízené samy od sebe.

Prodloužený víkend má oproti týdenní dovolené jednu výhodu: neplánuje se měsíce dopředu. Rozhodnete se týden předem, sbalíte jednu tašku a v pátek odpoledne sedíte u kamen. Právě na takové pobyty jsou [last-minute nabídky](/inspirace/jak-funguje-last-minute-ubytovani) stavěné.

## Tři dny stačí, když je neprojezdíte

Nejčastější chyba je vybrat chalupu podle fotek a až potom se podívat, kde vlastně stojí. Čtyři hodiny v pátečních kolonách a čtyři hodiny v neděli zpátky vám z tří dnů ukrojí jeden. Na prodloužený víkend platí jednoduché pravidlo: čím kratší pobyt, tím kratší cesta.

Z Prahy se dá pohodlně dojet do [středních Čech](/pobyty/stredni-cechy), na Vysočinu nebo do jižních Čech. Z Brna je to obráceně, tam je Vysočina za rohem a Beskydy na dvě hodiny. Tipy na chalupy kousek od hlavního města máme v článku [Chalupa do hodiny z Prahy](/inspirace/chalupa-do-hodiny-z-prahy).

::nabidky{category="chalupa" text="Prohlédnout volné chalupy"}

## Koncem října vybíráte hlavně podle tepla

V létě je jedno, jak se v chalupě topí. Na konci října je to první věc, na kterou se ptejte. Kamna na dřevo jsou romantická, ale než je roztopíte a než chalupa po týdnu bez lidí prohřeje, může být večer. Elektrické přímotopy nebo tepelné čerpadlo jsou méně fotogenické, zato fungují hned po příjezdu.

Ideální kombinace je obojí: stálé topení, které drží základní teplotu, a k tomu [krb](/pobyty/s-krbem) na večer. Hodí se zeptat, jestli je dřevo v ceně a kolik ho je. Tři večery u ohně spotřebují víc, než byste čekali.

Kdo chce podzim vidět v plné síle, ať míří do listnatých kopců. Jižní Čechy mají v tuhle dobu rybníky v mlze a lesy plné barev, na výběr je i [chalupa s krbem v jižních Čechách](/pobyty/jizni-cechy/s-krbem). Vysočina je o něco drsnější a tišší. Tam, kde chcete první sníh, jsou na řadě [pobyty na horách](/pobyty/pobyty-na-horach).

![Srub v lese s houpací sítí mezi stromy](https://images.unsplash.com/photo-1499696010180-025ef6e1a8f9?w=1200&q=80&auto=format&fit=crop)

## Jak si tři dny rozvrhnout, aby nebyly jen o cestě

Tři dny jsou přesně tolik, aby se dal každý věnovat něčemu jinému. Zkuste je od sebe oddělit:

| Den | Co s ním | Proč |
| --- | --- | --- |
| Příjezd | Roztopit, nakoupit, nikam nejít | Večer po cestě nemá nikdo chuť na program |
| Prostřední den | Jeden pořádný výlet, ne tři malé | Tma je v pět, do té doby chcete být zpátky |
| Odjezd | Dlouhá snídaně a brzký odjezd | Kolony se o svátkovém víkendu tvoří už po obědě |

Prostřední den je ten, kvůli kterému jedete. Vyberte si dopředu jednu trasu nebo jedno místo a zbytek nechte být. Podzimní odpoledne je krátké a pokus stihnout rozhlednu, hrad i vinný sklep skončí ve tmě na parkovišti.

## Na co myslet před rezervací

- **Minimální délka pobytu.** Někteří majitelé pronajímají na podzim jen na víkend, jiní mají naopak minimum tři noci. Filtr podle termínu vám ukáže jen to, co v daném termínu skutečně jde.
- **Časy příjezdu a odjezdu.** Pozdní příjezd v pátek večer je u chalup běžný, ale ne samozřejmý. Když víte, že dorazíte po setmění, napište to majiteli dopředu.
- **Nákup po cestě.** Větší obchody mají o státním svátku zavřeno a vesnická samoobsluha o víkendu zavírá v poledne. Základ nakupte cestou nebo den předem.
- **Co je v chalupě.** Ručníky, ložní prádlo, dřevo a základní koření. Na tři dny se nevyplatí vézt půl domácnosti, takže je dobré vědět, co na místě je.
- **Kdo jede s vámi.** Chalupa pro čtyři je na prodloužený víkend s druhým párem jiný pobyt než pro rodinu s malými dětmi. Ověřte počet postelí a jestli je dům vhodný [pro děti](/pobyty/pro-deti) nebo [pro psy](/pobyty/pro-psy).

## Proč právě teď a ne v lednu

Konec října je poslední chvíle, kdy se dá jet na chalupu bez zimního vybavení. Ještě se chodí po lese v botách, ne ve sněžnicích, ještě se sedí venku s dekou a ještě se dá večer opékat na ohništi. V lednu je to jiný pobyt s jinými nároky.

A po Vánocích, kdy se všichni chtějí někam schovat, je vyprodáno. Podzimní prodloužený víkend je klidnější, levnější a chalupy jsou ještě volné.

## Vyberte si chalupu na prodloužený víkend

Pět dní volna je za měsíc. Stačí vybrat chalupu blízko, s topením, které funguje hned, a s jedním výletem v plánu. Prohlédněte si aktuálně volné [chalupy](/pobyty/chalupy) a rezervujte, dokud je z čeho vybírat:

::cta{text="Prohlédnout všechny pobyty"}`,
}
